import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import { TxSelectors } from "src/redux/Tx/selectors";
import { TxActions } from "src/redux/Tx/actions";
import { TableActions } from "src/redux/Table/actions";
import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { SettingsSelectors } from "src/redux/Settings/selectors";

import DatePickerEditor from "src/components/common/AgGrid/DatePicker";
import TruTooltip from "src/components/common/AgGrid/TruTooltip";
import TxGroupExpandRenderer from "./TxGroupExpandRenderer";
import ConstituentsRenderer from "./ConstituentsRenderer";
import * as Validators from "src/utils/validators";
import {
    RowClassRules,
    CommonColumnDefs,
    getTXPreviewColumnDefs,
} from "./column_defs";
import DeleteRenderer from "src/components/common/AgGrid/DeleteRenderer";
import { REQUEST_STATES } from "src/redux/Tx/constants";

import "src/styles/agGridCustom.scss";
import styles from "./styles.module.scss";
import { handlePopupDisappear } from "src/utils/scrollUtil";
import { EXPORT_FORMAT } from "src/components/common/AgGrid/formatters";
import { ROLE } from "src/redux/TopBar/constants";

const rowsToUpdate = [];
let timerForUpdate = null;

const Table = () => {
    const gridApiRef = useRef(null);
    const dispatch = useDispatch();

    // Select data from Redux store
    const rowData = useSelector(TxSelectors.TxInitRowData);
    const sendingState = useSelector(TxSelectors.TxInitSendingState);
    const isReadyToSubmit = useSelector(TxSelectors.TxInitIsReady);
    const role = useSelector(TopBarSelectors.Role);
    const userSettings = useSelector(SettingsSelectors.UserSettings);
    const listNames = useSelector(SettingsSelectors.SelectableListNames);
    const featureFlags = useSelector(SettingsSelectors.FeatureFlags);

    // Store previous values for comparison
    const prevSendingState = useRef(sendingState);
    const prevIsReadyToSubmit = useRef(isReadyToSubmit);

    // Dispatch actions
    const editData = useCallback(
        (rows) => dispatch(TxActions.UpdateInitRow(rows)),
        [dispatch]
    );
    const deleteRow = useCallback(
        (rowId) => dispatch(TxActions.DeleteInitRow(rowId)),
        [dispatch]
    );
    const startEditingCell = useCallback(
        () => dispatch(TableActions.StartEditingCell()),
        [dispatch]
    );
    const stopEditingCell = useCallback(
        () => dispatch(TableActions.StopEditingCell()),
        [dispatch]
    );

    // Effect to handle cell refresh on state changes
    useEffect(() => {
        const isSendCancelled =
            prevSendingState.current === REQUEST_STATES.SENDING &&
            sendingState === REQUEST_STATES.NONE;
        const isSendDisabled = prevIsReadyToSubmit.current && !isReadyToSubmit;

        if (isSendCancelled || isSendDisabled) {
            gridApiRef.current?.refreshCells();
        }

        prevSendingState.current = sendingState;
        prevIsReadyToSubmit.current = isReadyToSubmit;
    }, [sendingState, isReadyToSubmit]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            gridApiRef.current = null;
            if (timerForUpdate) {
                clearInterval(timerForUpdate);
            }
        };
    }, []);

    const handleCellValueChange = useCallback(
        (params) => {
            rowsToUpdate.push(
                Validators.prepareRowEdit(
                    params.data,
                    params.colDef.field,
                    featureFlags,
                    false
                )
            );
            if (timerForUpdate) clearInterval(timerForUpdate);
            timerForUpdate = setInterval(() => {
                const length = rowsToUpdate.length;
                if (length > 0) {
                    const list = rowsToUpdate.splice(0, length);
                    editData(list);
                } else {
                    clearInterval(timerForUpdate);
                }
            }, 20);
        },
        [featureFlags, editData]
    );

    const handleGridReady = useCallback((params) => {
        gridApiRef.current = params.api;
    }, []);

    const handleTableScroll = useCallback((e) => {
        handlePopupDisappear(e, ["Create ValueDate"]);
    }, []);

    const customRenderers = useMemo(
        () => ({
            deleteButton: DeleteRenderer,
            customTooltip: TruTooltip,
            datePicker: DatePickerEditor,
            txGroupExpand: TxGroupExpandRenderer,
            constituentsRenderer: ConstituentsRenderer,
        }),
        []
    );

    const isPortfolioTx = useMemo(
        () =>
            rowData &&
            rowData.length > 0 &&
            rowData.some((row) => row.parentId !== undefined),
        [rowData]
    );

    const contextParams = useMemo(
        () => ({
            deleteRow,
            role,
            preferredCcy: userSettings.preferredCcy,
            listNames,
            featureFlags,
            isPortfolioTx,
        }),
        [
            deleteRow,
            role,
            userSettings.preferredCcy,
            listNames,
            featureFlags,
            isPortfolioTx,
        ]
    );

    const treeDataProps = useMemo(
        () =>
            isPortfolioTx
                ? {
                      treeData: true,
                      animateRows: true,
                      groupDefaultExpanded: -1,
                      groupDisplayType: "custom",
                      getDataPath: (data) => {
                          if (data.parentId === null) {
                              return [data.SecurityId];
                          }
                          return [data.parentId, data.SecurityId];
                      },
                  }
                : {},
        [isPortfolioTx]
    );

    const columnDefs = useMemo(
        () => getTXPreviewColumnDefs(role === ROLE.LENDER, isPortfolioTx),
        [role, isPortfolioTx]
    );

    return (
        <div className={["ag-theme-alpine-dark", styles.table].join(" ")}>
            <AgGridReact
                rowHeight={40}
                headerHeight={40}
                enableRangeSelection
                stopEditingWhenCellsLoseFocus
                getRowId={(params) => params.data.id}
                enableFillHandle
                undoRedoCellEditing
                undoRedoCellEditingLimit={10}
                rowBuffer={100}
                defaultColDef={CommonColumnDefs}
                columnDefs={columnDefs}
                rowData={rowData}
                rowClassRules={RowClassRules}
                onCellValueChanged={handleCellValueChange}
                tooltipShowDelay={1}
                components={customRenderers}
                context={contextParams}
                onGridReady={handleGridReady}
                onBodyScroll={handleTableScroll}
                onCellEditingStarted={startEditingCell}
                onCellEditingStopped={stopEditingCell}
                onSortChanged={(e) => e.api.refreshCells()}
                rowSelection={"multiple"}
                defaultCsvExportParams={{ onlySelected: true }}
                defaultExcelExportParams={{ onlySelected: true }}
                suppressCopyRowsToClipboard={true}
                excelStyles={EXPORT_FORMAT.EXCEL}
                {...treeDataProps}
            />
        </div>
    );
};

export default Table;
